/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 80, Figma 08·12): the cameras of a phone are named
 * "Фронтальная" / "Основная" instead of what the system calls them
 * ("Front Camera", "camera2 1, facing back", …). Other names stay.
 */

import { useEffect, useMemo } from "react";
import { BehaviorSubject } from "rxjs";

import {
  type DeviceLabel,
  type MediaDevice,
  type SelectedDevice,
} from "../state/MediaDevices";
import { pick, type IonText } from "./callErrorTexts";

export const CAMERA_TEXTS = {
  front: { ru: "Фронтальная", en: "Front" },
  back: { ru: "Основная", en: "Back" },
} satisfies Record<string, IonText>;

const FRONT = /\b(front|user)\b|фронт/i;
const BACK = /\b(back|rear|environment)\b|задн|основн/i;

/** The name of the design, or the system's name when it is not a phone's. */
export function cameraName(name: string, language: string | undefined): string {
  if (FRONT.test(name)) return pick(CAMERA_TEXTS.front, language);
  if (BACK.test(name)) return pick(CAMERA_TEXTS.back, language);
  return name;
}

function rename(
  available: Map<string, DeviceLabel>,
  language: string | undefined,
): Map<string, DeviceLabel> {
  const names = new Map<string, DeviceLabel>();
  for (const [id, label] of available)
    names.set(
      id,
      label.type === "name"
        ? { type: "name", name: cameraName(label.name, language) }
        : label,
    );
  // Two cameras of the same side (e.g. wide and ultra-wide) keep their own
  // names, else the list would show two "Основная"
  const counts = new Map<string, number>();
  for (const label of names.values())
    if (label.type === "name")
      counts.set(label.name, (counts.get(label.name) ?? 0) + 1);
  for (const [id, label] of names)
    if (label.type === "name" && (counts.get(label.name) ?? 0) > 1)
      names.set(id, available.get(id)!);
  return names;
}

/** The camera device with the names of the design, for DeviceSelection. */
export function useIonCameraNames(
  device: MediaDevice<DeviceLabel, SelectedDevice>,
  language: string | undefined,
): MediaDevice<DeviceLabel, SelectedDevice> {
  const available$ = useMemo(
    () =>
      new BehaviorSubject(
        // A plain observable in tests (deviceStub) has no value yet; the
        // subscription below brings it
        rename(
          (device.available$ as { value?: Map<string, DeviceLabel> }).value ??
            new Map(),
          language,
        ),
      ),
    [device, language],
  );
  useEffect(() => {
    const s = device.available$.subscribe((a) =>
      available$.next(rename(a, language)),
    );
    return (): void => s.unsubscribe();
  }, [device, language, available$]);
  return useMemo(
    () => ({
      available$,
      selected$: device.selected$,
      select: (id: string): void => device.select(id),
    }),
    [device, available$],
  );
}
